import type {
  ApiErrorDetail,
  ApiTaskStatus,
  AlertResource,
  AlertSourceContext,
  AlertTrigger,
  AsyncTaskCreated,
  BacktestExecution,
  BacktestResult,
  BacktestRun,
  BacktestSpec,
  BacktestValidation,
  Bar,
  ChartIndicatorId,
  ChartIndicatorParams,
  CarryQueryResponse,
  CarryUniverse,
  Condition,
  CrossVenueSpreadSpec,
  DerivativeHistoryResponse,
  DerivativesSnapshot,
  DynamicWatchlistResource,
  EventStudyExecution,
  EventStudyResult,
  EventStudyRun,
  EventStudySpec,
  EventStudyValidation,
  FairValueQueryResponse,
  FairValueUniverse,
  Instrument,
  InstrumentCatalogPage,
  IndicatorMethodologyResponse,
  IndicatorSeriesResponse,
  MarketCompareResponse,
  MarketHistoryPage,
  MarketDataMode,
  MarketMetadata,
  MarketBarEventPayload,
  MarketQuoteQueryResponse,
  MarketQuoteUniverse,
  RealtimeIndicatorLatest,
  RealtimeIndicatorRegistration,
  MarketSyncStatus,
  MarketSyncTriggerResponse,
  MarketTickPayload,
  MarketType,
  ScanDefinitionCreate,
  ScanDefinitionResource,
  ScanDefinitionRunCreated,
  ScanDefinitionUpdate,
  ScanRun,
  StrategyRadarResponse,
  UpstreamStatusResponse,
  Venue,
  VenueId,
  WorkspaceDeleteResult,
  WorkspaceResource,
  WorkspaceState,
  WorkspaceViewSummary
} from './types'
import type { RealtimeClient } from './realtime'
import type { MarketCapabilitiesResponse } from './marketCapabilities'
import type { OrderBookSnapshotPayload } from './orderBookModel.ts'
import { normalizeApiErrorDetail } from './apiError.ts'
import { broadcastAuthRequired } from './authEvents.ts'
import { rethrowAfterAuthSessionProbe } from './authSessionProbe.ts'
import { alignIndicatorSeriesToBars } from './chartModel'
import {
  bindChartIndicatorSeries,
  buildChartIndicatorRequestPlan,
  type IndicatorQueryRequest
} from './chartIndicatorRegistry.ts'
import type {
  ConditionPreviewResult,
  ConditionValidationResult
} from './conditionValidationFlow.ts'
import { resolveApiUrl } from './runtimeConfig'
import { buildSpreadAlertCreatePayload } from './spreadAlertModel'
import { workspaceSaveRequest } from './workspaceApiContract.ts'

export class ApiClientError extends Error {
  readonly code: string
  readonly traceId?: string
  readonly details?: unknown
  readonly latest?: unknown
  readonly status: number

  constructor(status: number, detail: ApiErrorDetail) {
    super(detail.message)
    this.name = 'ApiClientError'
    this.status = status
    this.code = detail.code
    this.traceId = detail.trace_id
    this.details = detail.details
    this.latest = detail.latest
  }
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const resolvedUrl = resolveApiUrl(url)
  const traceId = crypto.randomUUID()
  let response: Response
  try {
    response = await fetch(resolvedUrl, {
      ...options,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Trace-Id': traceId, ...(options?.headers || {}) }
    })
  } catch (error) {
    return rethrowAfterAuthSessionProbe(error)
  }
  if (!response.ok) {
    broadcastAuthRequired(response.status, 'http')
    const payload: unknown = await response.json().catch(() => null)
    const detail = normalizeApiErrorDetail(
      response.status,
      payload,
      response.headers.get('X-Trace-Id'),
      traceId
    )
    throw new ApiClientError(response.status, detail)
  }
  if (response.status === 204) return undefined as T
  return response.json()
}

export function getVenues(signal?: AbortSignal) {
  return request<{ items: Venue[]; mode: MarketDataMode }>('/v1/market/venues', { signal })
}

export function getUpstreamStatus(signal?: AbortSignal) {
  return request<UpstreamStatusResponse>('/v1/market/upstreams/status', {
    signal
  })
}

export function getMarketSyncStatus(signal?: AbortSignal) {
  return request<MarketSyncStatus>('/v1/market/sync/status', { signal })
}

export function triggerMarketSync(signal?: AbortSignal) {
  return request<MarketSyncTriggerResponse>('/v1/market/sync', {
    method: 'POST',
    signal
  })
}

export function getMarketCapabilities(signal?: AbortSignal) {
  return request<MarketCapabilitiesResponse>('/v1/market/capabilities', {
    signal
  })
}

export function getIndicatorMethodologies(signal?: AbortSignal) {
  return request<IndicatorMethodologyResponse>('/v1/indicators', {
    signal
  })
}

export function getStrategyRadar(refresh = false, signal?: AbortSignal) {
  return request<StrategyRadarResponse>(`/v1/strategy-radar?refresh=${String(refresh)}`, {
    signal
  })
}

export function getInstruments(
  options: {
    venue?: VenueId
    marketType?: MarketType
    query?: string
    status?: string
    cursor?: string
    limit?: number
  } = {},
  signal?: AbortSignal
): Promise<InstrumentCatalogPage> {
  const query = new URLSearchParams({ limit: String(options.limit ?? 50) })
  if (options.venue) query.set('venue', options.venue)
  if (options.marketType) query.set('market_type', options.marketType)
  if (options.query) query.set('query', options.query)
  if (options.status !== undefined) query.set('status', options.status)
  if (options.cursor) query.set('cursor', options.cursor)
  return request<InstrumentCatalogPage>(`/v1/market/instruments?${query}`, { signal })
}

export function getMarketQuotes(
  universes: MarketQuoteUniverse[],
  timeframe = '4h',
  signal?: AbortSignal
) {
  return request<MarketQuoteQueryResponse>('/v1/market/quotes/query', {
    method: 'POST',
    signal,
    body: JSON.stringify({ universes, timeframe })
  })
}

export function getMarketCarry(
  universes: CarryUniverse[],
  timeframe = '4h',
  signal?: AbortSignal
) {
  return request<CarryQueryResponse>('/v1/market/carry/query', {
    method: 'POST',
    signal,
    body: JSON.stringify({ universes, timeframe })
  })
}

export function getMarketFairValue(
  universes: FairValueUniverse[],
  signal?: AbortSignal
) {
  return request<FairValueQueryResponse>('/v1/market/fair-value/query', {
    method: 'POST',
    signal,
    body: JSON.stringify({ universes })
  })
}

export type MarketTickSnapshotResponse = {
  status: 'available' | 'stale' | 'unavailable'
  item: MarketTickPayload | null
  topic: string
  age_ms: number | null
  is_current: boolean
  is_tradeable_price: boolean
  stream: Record<string, unknown> | null
  warning: string | null
}

export function getLatestMarketTick(
  venue: VenueId,
  instrument: string,
  marketType: MarketType,
  signal?: AbortSignal
) {
  const query = new URLSearchParams({ venue, instrument, market_type: marketType })
  return request<MarketTickSnapshotResponse>(`/v1/market/ticks/latest?${query}`, { signal })
}

export type OrderBookLatestResponse = {
  status: 'available' | 'syncing' | 'stale' | 'unavailable'
  item: OrderBookSnapshotPayload | null
  topic: string
  age_ms: number | null
  is_current: boolean
  authority: 'exchange_l2'
  delivery: 'coalesced_full_top_n'
  runtime_scope: 'single_process'
  warning: string | null
}

export function getLatestOrderBook(
  venue: VenueId,
  instrument: string,
  marketType: MarketType,
  signal?: AbortSignal
) {
  const query = new URLSearchParams({
    venue,
    instrument,
    market_type: marketType
  })
  return request<OrderBookLatestResponse>(
    `/v1/market/order-book/latest?${query}`,
    { signal }
  )
}

export type RealtimeBarSnapshotResponse = {
  status: 'available' | 'stale' | 'unavailable'
  item: MarketBarEventPayload | null
  topic: string
  age_ms: number | null
  is_current: boolean
  authority: 'display_only'
  warning: string | null
}

export function getLatestRealtimeBar(
  venue: VenueId,
  instrument: string,
  marketType: MarketType,
  timeframe: string,
  signal?: AbortSignal
) {
  const query = new URLSearchParams({
    venue,
    instrument,
    market_type: marketType,
    timeframe
  })
  return request<RealtimeBarSnapshotResponse>(
    `/v1/market/bars/realtime/latest?${query}`,
    { signal }
  )
}

export function registerRealtimeIndicators(
  venue: VenueId,
  instrument: string,
  timeframe: string,
  marketType: MarketType,
  indicators: readonly IndicatorQueryRequest[],
  signal?: AbortSignal
) {
  return request<RealtimeIndicatorRegistration>('/v1/indicator-series/realtime/register', {
    method: 'POST',
    signal,
    body: JSON.stringify({
      universe: universe(venue, instrument, timeframe, marketType),
      bars: 500,
      indicators
    })
  })
}

export function getLatestRealtimeIndicators(
  seriesKey: string,
  signal?: AbortSignal
) {
  const query = new URLSearchParams({ series_key: seriesKey })
  return request<RealtimeIndicatorLatest>(
    `/v1/indicator-series/realtime/latest?${query}`,
    { signal }
  )
}

export function getDerivatives(venue: VenueId, instrument: string, marketType: MarketType, signal?: AbortSignal) {
  const query = new URLSearchParams({ venue, instrument, market_type: marketType })
  return request<DerivativesSnapshot>(`/v1/market/derivatives?${query}`, { signal })
}

export function getDerivativesHistory(
  venue: VenueId,
  instrument: string,
  metric: 'funding_rate' | 'open_interest',
  options: {
    interval?: 'settlement' | '5m' | '15m' | '30m' | '1h' | '4h' | '1D'
    start?: string
    end?: string
    cursor?: string
    limit?: number
  } = {},
  signal?: AbortSignal
) {
  const query = new URLSearchParams({
    venue,
    instrument,
    metric,
    interval: options.interval ?? (metric === 'funding_rate' ? 'settlement' : '4h'),
    limit: String(options.limit ?? 200)
  })
  if (options.start) query.set('start', options.start)
  if (options.end) query.set('end', options.end)
  if (options.cursor) query.set('cursor', options.cursor)
  return request<DerivativeHistoryResponse>(`/v1/market/derivatives/history?${query}`, { signal })
}

export function getMarketCompare(
  instrument: string,
  marketType: MarketType,
  timeframe: string,
  venues: VenueId[],
  limit = 240,
  signal?: AbortSignal
) {
  const query = new URLSearchParams({
    instrument,
    market_type: marketType,
    timeframe,
    limit: String(limit)
  })
  venues.forEach(venue => query.append('venues', venue))
  return request<MarketCompareResponse>(`/v1/market/compare?${query}`, { signal })
}

export async function getMarket(
  venue: VenueId,
  instrument: string,
  timeframe: string,
  marketType: MarketType,
  signal?: AbortSignal,
  options: {
    includeDerivatives?: boolean
    forceRefreshBars?: boolean
    indicators?: readonly ChartIndicatorId[]
    indicatorParams?: ChartIndicatorParams
  } = {}
) {
  const query = new URLSearchParams({ venue, instrument, timeframe, market_type: marketType, limit: '500' })
  if (options.forceRefreshBars) query.set('refresh', 'true')
  const bars = await request<{ items: Bar[]; metadata: MarketMetadata }>(`/v1/market/bars?${query}`, { signal })
  const indicatorPlan = buildChartIndicatorRequestPlan(
    options.indicators ?? ['ema20', 'ema50'],
    options.indicatorParams
  )
  const indicatorsPromise = request<IndicatorSeriesResponse>('/v1/indicator-series/query', {
      method: 'POST',
      signal,
      body: JSON.stringify({
        universe: { venue, instrument, market_type: marketType, timeframe },
        bars: 500,
        indicators: indicatorPlan.requests
      })
    })
  const derivativesPromise = marketType === 'perpetual' && options.includeDerivatives !== false
    ? getDerivatives(venue, instrument, marketType, signal)
    : Promise.resolve<DerivativesSnapshot | null>(null)
  const [indicatorOutcome, derivativesOutcome] = await Promise.allSettled([indicatorsPromise, derivativesPromise])
  if (signal?.aborted) throw signal.reason || new DOMException('请求已取消', 'AbortError')
  const indicatorBinding = indicatorOutcome.status === 'fulfilled'
    ? bindChartIndicatorSeries(indicatorPlan, indicatorOutcome.value)
    : null
  const indicators = indicatorOutcome.status === 'fulfilled' && indicatorBinding
    ? alignIndicatorSeriesToBars(bars.items, {
        ...indicatorOutcome.value,
        series: indicatorBinding.series
      })
    : { times: [], series: {}, methodology_version: '' }
  const derivatives = derivativesOutcome.status === 'fulfilled' ? derivativesOutcome.value : null
  return {
    ...bars,
    indicators,
    derivatives,
    indicatorWarning: indicatorOutcome.status === 'rejected'
      ? `指标暂不可用：${errorMessage(indicatorOutcome.reason)}`
      : indicatorBinding?.warnings.length
        ? `指标部分不可用：${indicatorBinding.warnings.join('；')}`
        : '',
    derivativesWarning: derivativesOutcome.status === 'rejected' ? `衍生品快照暂不可用：${errorMessage(derivativesOutcome.reason)}` : ''
  }
}

export function getMarketHistory(
  venue: VenueId,
  instrument: string,
  timeframe: string,
  marketType: MarketType,
  options: { cursor?: string; start?: string; end?: string; limit?: number } = {},
  signal?: AbortSignal
) {
  const query = new URLSearchParams({
    venue,
    instrument,
    timeframe,
    market_type: marketType,
    limit: String(options.limit ?? 120)
  })
  if (options.cursor) query.set('cursor', options.cursor)
  if (options.start) query.set('start', options.start)
  if (options.end) query.set('end', options.end)
  return request<MarketHistoryPage>(`/v1/market/history?${query}`, { signal })
}

export function validateCondition(condition: Condition, venue: VenueId, instrument: string, timeframe: string, marketType: MarketType, signal?: AbortSignal) {
  return request<ConditionValidationResult>('/v1/conditions/validate', {
    method: 'POST', signal, body: JSON.stringify({ condition, universe: universe(venue, instrument, timeframe, marketType) })
  })
}

export function previewCondition(condition: Condition, venue: VenueId, instrument: string, timeframe: string, marketType: MarketType, signal?: AbortSignal) {
  return request<ConditionPreviewResult>('/v1/conditions/preview', {
    method: 'POST', signal, body: JSON.stringify({ condition, universe: universe(venue, instrument, timeframe, marketType) })
  })
}

export async function runScan(
  condition: Condition,
  venue: VenueId,
  instruments: string[],
  timeframe: string,
  marketType: MarketType,
  signal?: AbortSignal,
  realtime?: RealtimeClient,
  onCreated?: (runId: string) => void
) {
  const created = await request<AsyncTaskCreated>('/v1/scans', {
    method: 'POST',
    signal,
    headers: { 'Idempotency-Key': crypto.randomUUID() },
    body: JSON.stringify({
      instruments,
      venue, market_type: marketType, timeframe, condition
    })
  })
  const runId = created.scan_run_id
  if (!runId) throw new Error('服务端未返回扫描任务 ID')
  onCreated?.(runId)
  const run = await waitForTask<ScanRun>(
    created.status_url,
    created.poll_after_ms,
    `scan.progress.${runId}`,
    signal,
    realtime
  )
  if (run.status !== 'SUCCEEDED') throw new Error(run.error?.message || `扫描任务状态：${run.status}`)
  return run
}

export async function resumeScan(runId: string, signal?: AbortSignal, realtime?: RealtimeClient): Promise<ScanRun> {
  const run = await waitForTask<ScanRun>(
    `/v1/scans/${encodeURIComponent(runId)}`,
    300,
    `scan.progress.${runId}`,
    signal,
    realtime
  )
  if (run.status !== 'SUCCEEDED') throw new Error(run.error?.message || `扫描任务状态：${run.status}`)
  return run
}

export async function listScanDefinitions(signal?: AbortSignal) {
  const result = await request<{ items: ScanDefinitionResource[] }>('/v1/scan-definitions', { signal })
  return result.items
}

export function createScanDefinition(payload: ScanDefinitionCreate, signal?: AbortSignal) {
  return request<ScanDefinitionResource>('/v1/scan-definitions', {
    method: 'POST',
    signal,
    body: JSON.stringify(payload)
  })
}

export function getScanDefinition(scanId: string, signal?: AbortSignal) {
  return request<ScanDefinitionResource>(`/v1/scan-definitions/${encodeURIComponent(scanId)}`, { signal })
}

export function updateScanDefinition(scanId: string, payload: ScanDefinitionUpdate, signal?: AbortSignal) {
  return request<ScanDefinitionResource>(`/v1/scan-definitions/${encodeURIComponent(scanId)}`, {
    method: 'PATCH',
    signal,
    body: JSON.stringify(payload)
  })
}

export function deleteScanDefinition(scanId: string, revision: number, signal?: AbortSignal) {
  const query = new URLSearchParams({ revision: String(revision) })
  return request<{ scan_id: string; deleted: true }>(`/v1/scan-definitions/${encodeURIComponent(scanId)}?${query}`, {
    method: 'DELETE',
    signal
  })
}

export function createScanDefinitionRun(scanId: string, signal?: AbortSignal) {
  return request<ScanDefinitionRunCreated>(`/v1/scan-definitions/${encodeURIComponent(scanId)}/runs`, {
    method: 'POST',
    signal,
    headers: { 'Idempotency-Key': crypto.randomUUID() }
  })
}

export async function runScanDefinition(
  scanId: string,
  signal?: AbortSignal,
  realtime?: RealtimeClient,
  onCreated?: (runId: string) => void
) {
  const created = await createScanDefinitionRun(scanId, signal)
  onCreated?.(created.scan_run_id)
  const run = await waitForTask<ScanRun>(
    created.status_url,
    created.poll_after_ms,
    `scan.progress.${created.scan_run_id}`,
    signal,
    realtime
  )
  if (run.status !== 'SUCCEEDED') throw new Error(run.error?.message || `扫描任务状态：${run.status}`)
  return run
}

export async function listDynamicWatchlists(signal?: AbortSignal) {
  const result = await request<{ items: DynamicWatchlistResource[] }>('/v1/dynamic-watchlists', { signal })
  return result.items
}

export function getDynamicWatchlist(watchlistId: string, signal?: AbortSignal) {
  return request<DynamicWatchlistResource>(`/v1/dynamic-watchlists/${encodeURIComponent(watchlistId)}`, { signal })
}

export function validateBacktest(payload: BacktestSpec, signal?: AbortSignal) {
  return request<BacktestValidation>('/v1/backtests/validate', {
    method: 'POST',
    signal,
    body: JSON.stringify(payload)
  })
}

export async function runBacktest(
  payload: BacktestSpec,
  signal?: AbortSignal,
  onCreated?: (runId: string) => void,
  realtime?: RealtimeClient
): Promise<BacktestExecution> {
  const created = await request<AsyncTaskCreated>('/v1/backtests', { method: 'POST', signal, headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify(payload) })
  const runId = created.run_id
  if (!runId) throw new Error('服务端未返回回测任务 ID')
  onCreated?.(runId)
  const run = await waitForTask<BacktestRun>(
    created.status_url,
    created.poll_after_ms,
    `backtest.progress.${runId}`,
    signal,
    realtime
  )
  if (run.status !== 'SUCCEEDED') throw new Error(run.error?.message || '回测未能完成')
  const result = await request<BacktestResult>(`${created.status_url}/results`, { signal })
  return { run_id: runId, run, result }
}

export async function resumeBacktest(runId: string, signal?: AbortSignal, realtime?: RealtimeClient): Promise<BacktestExecution> {
  const statusUrl = `/v1/backtests/${encodeURIComponent(runId)}`
  const run = await waitForTask<BacktestRun>(
    statusUrl,
    300,
    `backtest.progress.${runId}`,
    signal,
    realtime
  )
  if (run.status !== 'SUCCEEDED') throw new Error(run.error?.message || `回测任务状态：${run.status}`)
  const result = await request<BacktestResult>(`${statusUrl}/results`, { signal })
  return { run_id: runId, run, result }
}

export function validateEventStudy(payload: EventStudySpec, signal?: AbortSignal) {
  return request<EventStudyValidation>('/v1/event-studies/validate', {
    method: 'POST',
    signal,
    body: JSON.stringify(payload)
  })
}

export async function runEventStudy(
  payload: EventStudySpec,
  signal?: AbortSignal,
  onCreated?: (runId: string) => void,
  realtime?: RealtimeClient
): Promise<EventStudyExecution> {
  const created = await request<AsyncTaskCreated>('/v1/event-studies', {
    method: 'POST',
    signal,
    headers: { 'Idempotency-Key': crypto.randomUUID() },
    body: JSON.stringify(payload)
  })
  const runId = created.run_id
  if (!runId) throw new Error('服务端未返回事件研究任务 ID')
  onCreated?.(runId)
  const run = await waitForTask<EventStudyRun>(
    created.status_url,
    created.poll_after_ms,
    `event_study.progress.${runId}`,
    signal,
    realtime
  )
  if (run.status !== 'SUCCEEDED') throw new Error(run.error?.message || '事件研究未能完成')
  const result = await request<EventStudyResult>(`${created.status_url}/results`, { signal })
  return { run_id: runId, run, result }
}

export async function resumeEventStudy(runId: string, signal?: AbortSignal, realtime?: RealtimeClient): Promise<EventStudyExecution> {
  const statusUrl = `/v1/event-studies/${encodeURIComponent(runId)}`
  const run = await waitForTask<EventStudyRun>(
    statusUrl,
    300,
    `event_study.progress.${runId}`,
    signal,
    realtime
  )
  if (run.status !== 'SUCCEEDED') throw new Error(run.error?.message || `事件研究任务状态：${run.status}`)
  const result = await request<EventStudyResult>(`${statusUrl}/results`, { signal })
  return { run_id: runId, run, result }
}

export function createAlert(
  name: string,
  condition: Condition,
  venue: VenueId,
  instrument: string,
  timeframe: string,
  marketType: MarketType,
  sourceContext: AlertSourceContext = { origin: 'manual' },
  signal?: AbortSignal
) {
  return request<AlertResource>('/v1/alerts', {
    method: 'POST',
    signal,
    body: JSON.stringify({
      name,
      universe: universe(venue, instrument, timeframe, marketType),
      condition,
      trigger_mode: 'close_confirmed',
      source_context: sourceContext
    })
  })
}

export function createSpreadAlert(
  name: string,
  spreadSpec: CrossVenueSpreadSpec,
  sourceContext: AlertSourceContext = { origin: 'manual' },
  signal?: AbortSignal
) {
  return request<AlertResource>('/v1/alerts/spreads', {
    method: 'POST',
    signal,
    body: JSON.stringify(buildSpreadAlertCreatePayload(
      name,
      spreadSpec,
      sourceContext
    ))
  })
}

export async function listAlerts(signal?: AbortSignal) {
  const result = await request<{ items: AlertResource[] }>('/v1/alerts', { signal })
  return result.items
}

export function updateAlertState(alertId: string, state: AlertResource['state'], signal?: AbortSignal) {
  const query = new URLSearchParams({ state })
  return request<AlertResource>(`/v1/alerts/${encodeURIComponent(alertId)}?${query}`, {
    method: 'PATCH',
    signal
  })
}

export async function listAlertTriggers(alertId: string, signal?: AbortSignal) {
  const result = await request<{ items: AlertTrigger[] }>(`/v1/alerts/${encodeURIComponent(alertId)}/triggers`, { signal })
  return result.items
}

export function getWorkspace(workspaceId = 'default', signal?: AbortSignal) {
  return request<WorkspaceResource>(`/v1/workspaces/${encodeURIComponent(workspaceId)}`, { signal })
}

export async function listWorkspaces(signal?: AbortSignal) {
  const result = await request<{ items: WorkspaceViewSummary[] }>('/v1/workspaces', { signal })
  return result.items
}

export function createWorkspace(name: string, sourceWorkspaceId: string, sourceRevision: number, signal?: AbortSignal) {
  return request<WorkspaceViewSummary>('/v1/workspaces', {
    method: 'POST',
    signal,
    body: JSON.stringify({
      name,
      source_workspace_id: sourceWorkspaceId,
      source_revision: sourceRevision
    })
  })
}

export function createWorkspaceFromState(name: string, state: WorkspaceState, signal?: AbortSignal) {
  return request<WorkspaceViewSummary>('/v1/workspaces', {
    method: 'POST',
    signal,
    body: JSON.stringify({ name, state })
  })
}

export function saveWorkspace(workspaceId: string, revision: number, state: WorkspaceState, signal?: AbortSignal) {
  return request<WorkspaceResource>(`/v1/workspaces/${encodeURIComponent(workspaceId)}`, {
    ...workspaceSaveRequest(revision, state),
    signal,
  })
}

export function renameWorkspace(workspaceId: string, revision: number, name: string, signal?: AbortSignal) {
  return request<WorkspaceViewSummary>(`/v1/workspaces/${encodeURIComponent(workspaceId)}`, {
    method: 'PATCH',
    signal,
    body: JSON.stringify({ revision, name })
  })
}

export function deleteWorkspace(workspaceId: string, revision: number, signal?: AbortSignal) {
  const query = new URLSearchParams({ revision: String(revision) })
  return request<WorkspaceDeleteResult>(`/v1/workspaces/${encodeURIComponent(workspaceId)}?${query}`, {
    method: 'DELETE',
    signal
  })
}

function universe(venue: VenueId, instrument: string, timeframe: string, marketType: MarketType) {
  return { venue, instrument, market_type: marketType, timeframe }
}

function waitForTask<T extends { status: ApiTaskStatus }>(
  url: string,
  initialDelayMs: number,
  topic: string,
  signal?: AbortSignal,
  realtime?: RealtimeClient
): Promise<T> {
  const initialFallbackDelay = Math.min(Math.max(initialDelayMs, 100), 5_000)
  let fallbackDelay = initialFallbackDelay
  let timer: number | null = null
  let inFlight = false
  let refreshPending = false
  let settled = false
  let unsubscribeTopic = () => {}
  let unsubscribeStatus = () => {}

  return new Promise<T>((resolve, reject) => {
    const cleanup = () => {
      if (timer !== null) window.clearTimeout(timer)
      timer = null
      unsubscribeTopic()
      unsubscribeStatus()
      signal?.removeEventListener('abort', handleAbort)
    }
    const finish = (callback: () => void) => {
      if (settled) return
      settled = true
      cleanup()
      callback()
    }
    const handleAbort = () => {
      finish(() => reject(signal?.reason || new DOMException('请求已取消', 'AbortError')))
    }
    const schedule = () => {
      if (settled) return
      if (timer !== null) window.clearTimeout(timer)
      const connected = realtime?.isConnected() ?? false
      const delayMs = connected ? 5_000 : fallbackDelay
      if (!connected) fallbackDelay = Math.min(Math.round(fallbackDelay * 1.5), 5_000)
      timer = window.setTimeout(() => {
        timer = null
        void refresh()
      }, delayMs)
    }
    const wake = () => {
      if (settled) return
      if (timer !== null) window.clearTimeout(timer)
      timer = null
      if (inFlight) {
        refreshPending = true
        return
      }
      void refresh()
    }
    const refresh = async () => {
      if (settled) return
      if (inFlight) {
        refreshPending = true
        return
      }
      inFlight = true
      refreshPending = false
      try {
        const result = await request<T>(url, { signal })
        if (isTerminalTaskStatus(result.status)) {
          finish(() => resolve(result))
          return
        }
      } catch (error) {
        if (signal?.aborted) {
          handleAbort()
          return
        }
        if (!isRetryablePollingError(error)) {
          finish(() => reject(error))
          return
        }
      } finally {
        inFlight = false
      }
      if (settled) return
      if (refreshPending) {
        refreshPending = false
        timer = window.setTimeout(() => {
          timer = null
          void refresh()
        }, 0)
      } else {
        schedule()
      }
    }

    if (signal?.aborted) {
      handleAbort()
      return
    }
    signal?.addEventListener('abort', handleAbort, { once: true })
    if (realtime) {
      let initialStatus = true
      unsubscribeTopic = realtime.subscribe(topic, () => wake())
      unsubscribeStatus = realtime.onStatus(status => {
        const isInitialStatus = initialStatus
        initialStatus = false
        if (status.phase === 'CONNECTED') {
          fallbackDelay = initialFallbackDelay
          if (isInitialStatus) return
          wake()
        } else if (status.phase === 'RECONNECTING' || status.phase === 'REST_FALLBACK') {
          if (isInitialStatus) return
          wake()
        }
      })
    }
    wake()
  })
}

function isTerminalTaskStatus(status: ApiTaskStatus): boolean {
  return ['SUCCEEDED', 'FAILED', 'CANCELLED', 'TIMED_OUT'].includes(status)
}

function isRetryablePollingError(error: unknown): boolean {
  if (error instanceof ApiClientError) return error.status === 429 || error.status >= 500
  return error instanceof TypeError
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : '未知错误'
}
